import { Box, MenuItem, Stack, TextField, Typography } from "@mui/material";
import React from "react";
import {
  ABOUT_DESIGNATION_OPTIONS,
  AboutDesignation,
  buildAboutDesignation,
  getDefaultAboutRank,
} from "../../about/about_data";
import { toTitleCaseName } from "../../../utils/title_case";

export type AboutFormValues = {
  rank: string;
  name: string;
  designationType: AboutDesignation;
  designationDetail: string;
  place: string;
  img: string;
};

interface AboutFormFieldsProps {
  formValues: AboutFormValues;
  setFormValues: React.Dispatch<React.SetStateAction<AboutFormValues>>;
}

export function getEmptyAboutFormValues(): AboutFormValues {
  return {
    rank: String(getDefaultAboutRank("Coordinator")),
    name: "",
    designationType: "Coordinator",
    designationDetail: "",
    place: "",
    img: "",
  };
}

export default function AboutFormFields({
  formValues,
  setFormValues,
}: AboutFormFieldsProps) {
  const showDetailField =
    formValues.designationType === "Youth Director" ||
    formValues.designationType === "Others";

  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
          Leader Details
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Rank is auto-suggested from the designation, but you can still adjust it.
        </Typography>
      </Box>
      <TextField
        select
        fullWidth
        label="Designation"
        value={formValues.designationType}
        onChange={(event) => {
          const designationType = event.target.value as AboutDesignation;
          setFormValues((prev) => ({
            ...prev,
            designationType,
            designationDetail:
              designationType === "Youth Director"
                ? prev.designationDetail || "Secretary"
                : designationType === "Others"
                  ? prev.designationDetail
                  : "",
            rank: String(getDefaultAboutRank(designationType)),
          }));
        }}
      >
        {ABOUT_DESIGNATION_OPTIONS.map((designation) => (
          <MenuItem key={designation} value={designation}>
            {designation}
          </MenuItem>
        ))}
      </TextField>

      {showDetailField ? (
        <TextField
          fullWidth
          label={formValues.designationType === "Youth Director" ? "Youth Director Prefix" : "Custom Designation"}
          value={formValues.designationDetail}
          onChange={(event) => {
            setFormValues((prev) => ({
              ...prev,
              designationDetail: event.target.value,
            }));
          }}
          helperText={
            formValues.designationType === "Youth Director"
              ? `Displayed as ${buildAboutDesignation("Youth Director", formValues.designationDetail || "Secretary")}`
              : "Enter the custom designation for Others."
          }
        />
      ) : null}

      <Box display="flex" gap={2} flexWrap="wrap">
        <TextField
          fullWidth
          label="Rank"
          type="number"
          value={formValues.rank}
          onChange={(event) => setFormValues((prev) => ({ ...prev, rank: event.target.value }))}
          inputProps={{ min: 1, step: 1 }}
          helperText="Lower rank appears first."
        />
      </Box>

      <TextField
        fullWidth
        label="Name"
        value={formValues.name}
        onChange={(event) =>
          setFormValues((prev) => ({
            ...prev,
            name: toTitleCaseName(event.target.value),
          }))
        }
        helperText="Enter titles like Mrs or Br as part of the name."
      />

      <TextField
        fullWidth
        label="Place"
        value={formValues.place}
        onChange={(event) => setFormValues((prev) => ({ ...prev, place: event.target.value }))}
      />
    </Stack>
  );
}
